import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API, BACKEND_URL } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Calendar, Clock, Users, RefreshCw, Plus, Play,
  LogOut, ChevronRight, Video, Trash2, MoreVertical, FileText, Copy, ArrowRight, Mic, Settings,
  BookOpen, Shield, ScrollText, CreditCard, Clipboard, ChevronDown, Target, Sparkles, Loader2
} from "lucide-react";
import { motion } from "framer-motion";
import UsageIndicator from "@/components/UsageIndicator";
import GoalContextPopover from "@/components/meeting/GoalContextPopover";
import SourceSelector from "@/components/sources/SourceSelector";
import { format, parseISO, isToday, isTomorrow, differenceInHours, isPast, differenceInMinutes } from "date-fns";

const MEETING_URL_PATTERNS = [
  /zoom\.us\/j\//i,
  /meet\.google\.com\/[a-z-]+/i,
  /teams\.microsoft\.com\/l\/meetup-join/i,
  /teams\.live\.com\/meet/i,
  /whereby\.com\/[a-z0-9-]+/i,
  /webex\.com\//i,
  /gotomeeting\.com\//i,
  /join\.slack\.com\//i,
  /us\d+\.web\.zoom\.us\/j\//i,
];

function isMeetingUrl(text) {
  if (!text || typeof text !== "string") return false;
  const trimmed = text.trim();
  if (!trimmed) return false;
  return MEETING_URL_PATTERNS.some((p) => p.test(trimmed));
}

function stripHtml(html) {
  if (!html || typeof html !== "string") return "";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function extractMeetingUrl(text) {
  if (!text || typeof text !== "string") return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (isMeetingUrl(trimmed)) return trimmed;
  const urlMatch = trimmed.match(/(https?:\/\/[^\s]+)/);
  if (urlMatch && isMeetingUrl(urlMatch[1])) return urlMatch[1];
  return null;
}

function StatusPill({ startTime, endTime }) {
  try {
    const now = new Date();
    const start = parseISO(startTime);
    const end = parseISO(endTime);

    if (isPast(end)) {
      return <Badge variant="secondary">Completed</Badge>;
    }
    const mins = differenceInMinutes(start, now);
    if (mins <= 0 && !isPast(end)) {
      return <Badge variant="live" className="gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />Live</Badge>;
    }
    if (mins <= 60) {
      return <Badge variant="warning">In {mins}m</Badge>;
    }
    if (mins <= 1440) {
      const hours = Math.floor(mins / 60);
      return <Badge variant="outline">In {hours}h</Badge>;
    }
    return <Badge variant="outline">{format(start, "MMM d")}</Badge>;
  } catch {
    return <Badge variant="outline">Scheduled</Badge>;
  }
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [hasClipboardMeetingLink, setHasClipboardMeetingLink] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: "", meeting_url: "", goal: "", context: "", outcomes: ""
  });
  const [showContextFields, setShowContextFields] = useState(false);
  const [newEventPrepExpanded, setNewEventPrepExpanded] = useState(false);
  const [draftSourceIds, setDraftSourceIds] = useState([]);
  const [draftPrimaryId, setDraftPrimaryId] = useState(null);
  const [heroGoal, setHeroGoal] = useState("");
  const [heroContext, setHeroContext] = useState("");
  const [heroOutcomes, setHeroOutcomes] = useState("");
  const [heroShowContext, setHeroShowContext] = useState(false);
  const [heroPrepExpanded, setHeroPrepExpanded] = useState(false);
  const [heroAgendaItems, setHeroAgendaItems] = useState([]);
  const [heroGenerating, setHeroGenerating] = useState(false);
  const [creatingEvent, setCreatingEvent] = useState(false);
  const creatingEventRef = useRef(false);

  useEffect(() => {
    if (user && !user.onboarding_completed) {
      const completeOnboarding = async () => {
        try {
          await axios.post(`${API}/auth/onboarding-complete`, {}, { withCredentials: true });
        } catch {}
      };
      completeOnboarding();
    }
  }, [user]);

  const fetchEvents = async () => {
    const PAGE = 200;
    // Page through until has_more is false so we never silently truncate a user
    // with a large calendar. Safety cap of 20 pages (≈4,000 events) blocks a
    // runaway loop if the backend ever misreports has_more.
    const fetchAll = async (path) => {
      const acc = [];
      let offset = 0;
      for (let i = 0; i < 20; i++) {
        const resp = await axios.get(
          `${API}/events/${path}?limit=${PAGE}&offset=${offset}`,
          { withCredentials: true },
        );
        const data = resp.data || {};
        const page = data.events || [];
        acc.push(...page);
        if (!data.has_more || page.length === 0) break;
        offset += page.length;
      }
      return acc;
    };
    try {
      const [up, ps] = await Promise.all([fetchAll("upcoming"), fetchAll("past")]);
      setUpcomingEvents(up);
      setPastEvents(ps);
    } catch (err) {
      console.error("Failed to fetch events:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); }, []);

  useEffect(() => {
    if (!showAddEvent) return;
    let cancelled = false;
    (async () => {
      try {
        const text = await navigator.clipboard.readText();
        if (cancelled) return;
        setHasClipboardMeetingLink(!!extractMeetingUrl(text));
      } catch {
        setHasClipboardMeetingLink(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showAddEvent]);

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const url = extractMeetingUrl(text);
      if (url) {
        setNewEvent((prev) => ({ ...prev, meeting_url: url }));
        setHasClipboardMeetingLink(false);
        toast.success("Meeting link pasted");
      } else {
        toast.error("No meeting link found in clipboard");
      }
    } catch (err) {
      toast.error("Could not access clipboard");
    }
  };

  const syncCalendarEvents = async () => {
    setSyncing(true);
    try {
      const resp = await axios.post(`${API}/events/sync-calendar`, {}, { withCredentials: true });
      toast.success(resp.data.message || "Calendar synced");
      fetchEvents();
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Please reconnect your Google Calendar", {
          action: {
            label: "Reconnect",
            onClick: handleIntegrateCalendar,
          },
        });
      } else {
        toast.error("Failed to sync calendar");
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleIntegrateCalendar = () => {
    window.location.href = `${BACKEND_URL}/api/auth/google?prompt=consent`;
  };

  const createEvent = async (e) => {
    e.preventDefault();
    if (creatingEventRef.current) return;
    if (!newEvent.meeting_url?.trim()) {
      toast.error("Please add a meeting URL");
      return;
    }
    creatingEventRef.current = true;
    setCreatingEvent(true);
    try {
      const payload = {
        title: newEvent.title || null,
        meeting_url: newEvent.meeting_url.trim(),
        description: null,
        attendees: [],
      };
      const evResp = await axios.post(`${API}/events`, payload, { withCredentials: true });
      const eventId = evResp.data.event_id;
      const goal = (newEvent.goal || "").trim();
      const context = (newEvent.context || "").trim();
      const outcomes = (newEvent.outcomes || "").trim();
      if (goal || context || outcomes) {
        const agPayload = { goal: goal || null, context: context || null, desired_outcomes: outcomes || null };
        try {
          await axios.post(`${API}/agendas/${eventId}/upsert-and-generate`, agPayload, { withCredentials: true });
        } catch (e) {
          const isRouteNotFound = e.response?.status === 404 && (e.response?.data?.detail === "Not Found" || !e.response?.data?.detail);
          if (isRouteNotFound) {
            await axios.post(`${API}/agendas/${eventId}`, agPayload, { withCredentials: true });
            await axios.post(`${API}/agendas/${eventId}/generate`, {}, { withCredentials: true });
          } else throw e;
        }
      }
      if (draftSourceIds.length > 0) {
        await axios.post(
          `${API}/meetings/${eventId}/source-selection`,
          { selected_source_ids: draftSourceIds, primary_source_id: draftPrimaryId },
          { withCredentials: true }
        );
      }
      setShowAddEvent(false);
      setShowContextFields(false);
      setNewEvent({ title: "", meeting_url: "", goal: "", context: "", outcomes: "" });
      setDraftSourceIds([]);
      setDraftPrimaryId(null);
      fetchEvents();
      navigate(`/meetings/${eventId}/live`, { state: { autoJoin: true } });
    } catch (err) {
      toast.error("Failed to create meeting");
    } finally {
      creatingEventRef.current = false;
      setCreatingEvent(false);
    }
  };

  const deleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}`, { withCredentials: true });
      toast.success("Meeting deleted");
      fetchEvents();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleJoinMeeting = async (evt) => {
    const eventId = evt.event_id;
    try {
      const agdResp = await axios.get(`${API}/agendas/${eventId}`, { withCredentials: true }).catch(() => ({}));
      const agd = agdResp?.data;
      const hasItems = agd?.items?.length > 0;

      // Navigate immediately — don't block on agenda generation
      navigate(`/meetings/${eventId}/live`, { state: { autoJoin: true } });

      // Only generate if agenda items don't exist yet
      if (!hasItems) {
        const goal = (agd?.goal || "").trim();
        const context = (agd?.context || "").trim();
        const outcomes = (agd?.desired_outcomes || "").trim();
        if (goal || context || outcomes) {
          const agPayload = { goal: goal || null, context: context || null, desired_outcomes: outcomes || null };
          // Fire-and-forget — meeting page will fetch result when ready
          axios.post(`${API}/agendas/${eventId}/upsert-and-generate`, agPayload, { withCredentials: true })
            .catch(async (e) => {
              const isRouteNotFound = e.response?.status === 404 && (e.response?.data?.detail === "Not Found" || !e.response?.data?.detail);
              if (isRouteNotFound) {
                await axios.post(`${API}/agendas/${eventId}`, agPayload, { withCredentials: true });
                await axios.post(`${API}/agendas/${eventId}/generate`, {}, { withCredentials: true });
              }
            });
        }
      }
    } catch (err) {
      toast.error("Could not join meeting");
    }
  };

  const getTimeLabel = (startTime) => {
    try {
      const dt = parseISO(startTime);
      if (isToday(dt)) return "Today";
      if (isTomorrow(dt)) return "Tomorrow";
      const hours = differenceInHours(dt, new Date());
      if (hours < 48) return "In 2 days";
      return format(dt, "EEE, MMM d");
    } catch { return ""; }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const allUpcoming = upcomingEvents;
  const past = pastEvents;
  const heroEvent = allUpcoming[0] || null;
  const restUpcoming = allUpcoming.slice(1);
  const isHeroCalendarSynced = heroEvent?.google_event_id;

  useEffect(() => {
    if (!heroEvent?.event_id || !isHeroCalendarSynced) {
      setHeroGoal("");
      setHeroContext("");
      setHeroOutcomes("");
      setHeroShowContext(false);
      setHeroPrepExpanded(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/agendas/${heroEvent.event_id}`, { withCredentials: true });
        if (cancelled) return;
        if (resp.data) {
          setHeroGoal(resp.data.goal || "");
          setHeroContext(resp.data.context || "");
          setHeroOutcomes(resp.data.desired_outcomes || "");
          setHeroShowContext(!!(resp.data.context || resp.data.desired_outcomes));
          setHeroAgendaItems(resp.data.items || []);
        } else {
          setHeroGoal("");
          setHeroContext("");
          setHeroOutcomes("");
          setHeroShowContext(false);
          setHeroAgendaItems([]);
        }
      } catch {
        if (!cancelled) setHeroGoal("");
      }
    })();
    return () => { cancelled = true; };
  }, [heroEvent?.event_id, isHeroCalendarSynced]);

  useEffect(() => {
    if (!heroEvent?.event_id || !isHeroCalendarSynced || !heroGoal.trim()) return;
    const t = setTimeout(async () => {
      try {
        await axios.post(
          `${API}/agendas/${heroEvent.event_id}`,
          { goal: heroGoal.trim(), context: heroContext || null, desired_outcomes: heroOutcomes || null },
          { withCredentials: true }
        );
      } catch {
        toast.error("Failed to save goal");
      }
    }, 600);
    return () => clearTimeout(t);
  }, [heroEvent?.event_id, isHeroCalendarSynced, heroGoal, heroContext, heroOutcomes]);

  const generateHeroAgenda = async () => {
    if (!heroEvent?.event_id || !heroGoal.trim()) return;
    setHeroGenerating(true);
    const payload = { goal: heroGoal.trim(), context: heroContext || null, desired_outcomes: heroOutcomes || null };
    try {
      let resp;
      try {
        resp = await axios.post(`${API}/agendas/${heroEvent.event_id}/upsert-and-generate`, payload, { withCredentials: true });
      } catch (e) {
        const isRouteNotFound = e.response?.status === 404 && (e.response?.data?.detail === "Not Found" || !e.response?.data?.detail);
        if (isRouteNotFound) {
          await axios.post(`${API}/agendas/${heroEvent.event_id}`, payload, { withCredentials: true });
          resp = await axios.post(`${API}/agendas/${heroEvent.event_id}/generate`, {}, { withCredentials: true });
        } else {
          throw e;
        }
      }
      const items = resp.data?.items || [];
      setHeroAgendaItems(items);
      if (items.length > 0) toast.success(`Generated ${items.length} agenda items`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail) && detail[0]?.msg
          ? detail.map((e) => e.msg).join("; ")
          : detail?.message || err.message || "Failed to generate agenda";
      toast.error(msg);
    } finally {
      setHeroGenerating(false);
    }
  };

  const firstName = user?.name?.split(" ")[0] || "there";
  const sawDemoBeforeSignup = typeof window !== "undefined" && localStorage.getItem("shadow_demo_visited") === "true";

  return (
    <div className="min-h-screen mesh-bg">
      {/* Navbar */}
      <header className="fixed top-0 left-0 right-0 z-50 surface-nav">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2.5 cursor-pointer" data-testid="nav-logo" onClick={() => navigate("/dashboard")}>
              <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-white font-heading text-xs">S</span>
              </div>
              <span className="font-heading text-foreground text-base">Shadow</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <UsageIndicator />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-full h-8 gap-2 px-2.5" data-testid="user-menu-btn">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="text-[10px] bg-secondary text-muted-foreground">{user?.name?.[0] || "U"}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm text-muted-foreground hidden sm:inline">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl p-1.5">
                <DropdownMenuItem className="text-xs text-muted-foreground rounded-lg pointer-events-none">{user?.email}</DropdownMenuItem>
                <DropdownMenuItem asChild className="rounded-lg">
                  <Link to="/sources" className="cursor-pointer"><BookOpen className="w-4 h-4 mr-2" />Knowledge Base</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="rounded-lg">
                  <Link to="/settings" className="cursor-pointer"><CreditCard className="w-4 h-4 mr-2" />Billing & Invoice</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="rounded-lg">
                  <Link to="/privacy" className="cursor-pointer"><Shield className="w-4 h-4 mr-2" />Privacy Policy</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="rounded-lg">
                  <Link to="/terms" className="cursor-pointer"><ScrollText className="w-4 h-4 mr-2" />Terms of Service</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="rounded-lg text-destructive">
                  <LogOut className="w-4 h-4 mr-2" />Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="pt-24 pb-20 px-6 max-w-6xl mx-auto">
        {/* Header section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-[28px] text-foreground tracking-tight">
              Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}, {firstName}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {sawDemoBeforeSignup && allUpcoming.length === 0 && past.length === 0
                ? "Welcome back! You've seen Shadow in action — let's get you set up."
                : allUpcoming.length > 0
                ? `You have ${allUpcoming.length} upcoming meeting${allUpcoming.length !== 1 ? "s" : ""}`
                : "No upcoming meetings"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate("/demo")}
              className="rounded-full h-8 gap-1.5 text-xs px-3.5"
            >
              <Play className="w-3.5 h-3.5" />
              Try Demo
            </Button>
            {user?.calendar_connected ? (
              <Button
                data-testid="sync-calendar-btn"
                onClick={syncCalendarEvents}
                disabled={syncing}
                variant="outline"
                className="rounded-full h-8 gap-1.5 text-xs px-3.5"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? "Syncing..." : "Sync"}
              </Button>
            ) : (
              <Button
                data-testid="integrate-calendar-btn"
                onClick={handleIntegrateCalendar}
                variant="outline"
                className="rounded-full h-8 gap-1.5 text-xs px-3.5"
              >
                <Calendar className="w-3.5 h-3.5" />
                Connect Calendar
              </Button>
            )}
            <Button
              data-testid="add-meeting-btn"
              onClick={() => {
                setNewEvent({ title: "", meeting_url: "", goal: "", context: "", outcomes: "" });
                setShowContextFields(false);
                setNewEventPrepExpanded(false);
                setDraftSourceIds([]);
                setDraftPrimaryId(null);
                setShowAddEvent(!showAddEvent);
              }}
              className="rounded-full h-8 gap-1.5 text-xs px-3.5"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Meeting
            </Button>
          </div>
        </div>

        {/* Calendar connection nudge */}
        {!user?.calendar_connected && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="surface-card rounded-2xl p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 border-l-4 border-l-amber-400"
          >
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-foreground mb-0.5">Connect your Google Calendar</h3>
              <p className="text-xs text-muted-foreground">Shadow needs calendar access to see your meetings, prep agendas, and join calls automatically.</p>
            </div>
            <Button
              onClick={handleIntegrateCalendar}
              size="sm"
              className="rounded-lg h-8 gap-1.5 text-xs px-4 shrink-0"
            >
              <Calendar className="w-3.5 h-3.5" />
              Connect Calendar
            </Button>
          </motion.div>
        )}

        {/* Add meeting form */}
        {showAddEvent && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="surface-card rounded-2xl p-8 mb-10"
          >
            <h3 className="font-heading text-foreground text-lg mb-6">New Meeting</h3>
            <form onSubmit={createEvent} className="space-y-5">
              <div>
                <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Meeting URL</label>
                <div className="flex gap-2">
                  <input
                    data-testid="event-url-input"
                    value={newEvent.meeting_url}
                    onChange={(e) => setNewEvent({ ...newEvent, meeting_url: e.target.value })}
                    className="surface-input flex-1 h-10 px-4 rounded-xl text-sm text-foreground"
                    placeholder="Paste Zoom, Meet, or Teams link..."
                  />
                  {hasClipboardMeetingLink && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-xl h-10 px-4 shrink-0 gap-1.5 text-xs"
                      onClick={handlePasteFromClipboard}
                    >
                      <Clipboard className="w-3.5 h-3.5" />
                      Paste
                    </Button>
                  )}
                </div>
              </div>
              {!newEventPrepExpanded ? (
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setNewEventPrepExpanded(true)}
                >
                  <ChevronDown className="w-3 h-3" />
                  Add goal & sources
                </button>
              ) : (
                <div className="pt-4 border-t border-border space-y-4">
                  <div>
                    <label className="text-[11px] text-muted-foreground font-medium mb-1.5 block">What do you want to achieve?</label>
                    <input
                      data-testid="event-goal-input"
                      value={newEvent.goal}
                      onChange={(e) => setNewEvent({ ...newEvent, goal: e.target.value })}
                      className="surface-input w-full h-9 px-3 rounded-lg text-sm"
                      placeholder="e.g. Align on Q2 product priorities"
                    />
                  </div>
                  {!showContextFields ? (
                    <button
                      type="button"
                      className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground"
                      onClick={() => setShowContextFields(true)}
                    >
                      <ChevronDown className="w-3 h-3" />
                      Add more context
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="text-[11px] text-muted-foreground font-medium mb-1 block">Additional context</label>
                        <textarea
                          value={newEvent.context}
                          onChange={(e) => setNewEvent({ ...newEvent, context: e.target.value })}
                          className="surface-input w-full px-3 py-2 rounded-lg text-xs resize-none"
                          rows={2}
                          placeholder="Background info for a better agenda"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] text-muted-foreground font-medium mb-1 block">Desired outcomes</label>
                        <textarea
                          value={newEvent.outcomes}
                          onChange={(e) => setNewEvent({ ...newEvent, outcomes: e.target.value })}
                          className="surface-input w-full px-3 py-2 rounded-lg text-xs resize-none"
                          rows={2}
                          placeholder="What should be decided by the end?"
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    <SourceSelector
                      eventId={null}
                      compact
                      draftMode
                      selectedIds={draftSourceIds}
                      primaryId={draftPrimaryId}
                      onSelectedIdsChange={setDraftSourceIds}
                      onPrimaryIdChange={setDraftPrimaryId}
                    />
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="rounded-full h-8 text-xs"
                  onClick={() => {
                    setShowAddEvent(false);
                    setShowContextFields(false);
                    setNewEventPrepExpanded(false);
                    setNewEvent({ title: "", meeting_url: "", goal: "", context: "", outcomes: "" });
                    setDraftSourceIds([]);
                    setDraftPrimaryId(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={creatingEvent} className="rounded-full h-8 text-xs px-4 min-w-[100px]">
                  {creatingEvent ? (
                    <span className="flex items-center gap-1.5">
                      <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                      Starting...
                    </span>
                  ) : "Start Meeting"}
                </Button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Events */}
        {loading ? (
          <div className="flex justify-center py-24">
            <div className="w-6 h-6 border-2 border-border border-t-foreground rounded-full animate-spin" />
          </div>
        ) : allUpcoming.length === 0 && past.length === 0 ? (
          <div className="surface-card rounded-2xl p-16 text-center max-w-lg mx-auto">
            <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-5">
              <Calendar className="w-6 h-6 text-muted-foreground" />
            </div>
            <h3 className="font-heading text-foreground text-lg mb-2">No meetings yet</h3>
            <p className="text-muted-foreground text-sm mb-8 leading-relaxed">
              Connect your calendar to sync meetings,<br />or create one manually.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {user?.calendar_connected ? (
                <Button
                  data-testid="empty-sync-btn"
                  onClick={syncCalendarEvents}
                  disabled={syncing}
                  className="rounded-full h-9 px-5 text-sm"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  Sync Calendar
                </Button>
              ) : (
                <Button
                  data-testid="empty-integrate-btn"
                  onClick={handleIntegrateCalendar}
                  className="rounded-full h-9 px-5 text-sm"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Connect Calendar
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                setNewEvent({ title: "", meeting_url: "", goal: "", context: "", outcomes: "" });
                setShowContextFields(false);
                setNewEventPrepExpanded(false);
                  setDraftSourceIds([]);
                  setDraftPrimaryId(null);
                  setShowAddEvent(true);
                }}
                className="rounded-full h-9 px-5 text-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Meeting
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-14">
            {/* Hero: Next meeting */}
            {heroEvent && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div
                  className="surface-card-hover rounded-3xl p-8 md:p-10 cursor-pointer group"
                  onClick={() => {
                    try {
                      const ended = isPast(parseISO(heroEvent.end_time));
                      if (ended) navigate(`/meetings/${heroEvent.event_id}/review`);
                      else handleJoinMeeting(heroEvent);
                    } catch {
                      handleJoinMeeting(heroEvent);
                    }
                  }}
                  data-testid={`event-card-${heroEvent.event_id}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <StatusPill startTime={heroEvent.start_time} endTime={heroEvent.end_time} />
                      <span className="text-sm text-muted-foreground">{getTimeLabel(heroEvent.start_time)}</span>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`event-menu-${heroEvent.event_id}`} onClick={(e) => e.stopPropagation()}>
                          <MoreVertical className="w-4 h-4 text-muted-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-xl">
                        <DropdownMenuItem
                          data-testid={`delete-event-${heroEvent.event_id}`}
                          className="text-destructive rounded-lg"
                          onClick={(e) => { e.stopPropagation(); deleteEvent(heroEvent.event_id); }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <h2 className="font-heading text-xl sm:text-2xl text-foreground tracking-tight mb-3 line-clamp-2">
                    {heroEvent.title}
                  </h2>

                  {heroEvent.description && stripHtml(heroEvent.description) && (
                    <p className="text-muted-foreground text-sm mb-3 line-clamp-2 max-w-2xl">{stripHtml(heroEvent.description)}</p>
                  )}

                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mb-6">
                    <span className="flex items-center gap-1 font-mono">
                      <Clock className="w-3 h-3 shrink-0" />
                      {(() => {
                        try {
                          return `${format(parseISO(heroEvent.start_time), "h:mm a")} – ${format(parseISO(heroEvent.end_time), "h:mm a")}`;
                        } catch { return "Time TBD"; }
                      })()}
                    </span>
                    {heroEvent.attendees?.length > 0 && (
                      <>
                        <span className="text-muted-foreground/40">·</span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3 shrink-0" />
                          {heroEvent.attendees.length} attendee{heroEvent.attendees.length !== 1 ? "s" : ""}
                        </span>
                      </>
                    )}
                    {heroEvent.meeting_url && (
                      <>
                        <span className="text-muted-foreground/40">·</span>
                        <span className="flex items-center gap-1">
                          <Video className="w-3 h-3 shrink-0" />
                          {heroEvent.meeting_url.includes("zoom") ? "Zoom" : heroEvent.meeting_url.includes("meet.google") ? "Google Meet" : "Video call"}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(heroEvent.meeting_url);
                              toast.success("Meeting link copied");
                            }}
                            className="p-0.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </span>
                      </>
                    )}
                  </div>

                  {isHeroCalendarSynced && (
                    <div
                      className="mb-6"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!heroPrepExpanded ? (
                        <button
                          type="button"
                          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                          onClick={() => setHeroPrepExpanded(true)}
                        >
                          <ChevronDown className="w-3 h-3" />
                          Add goal & sources
                        </button>
                      ) : (
                        <div className="pt-4 border-t border-border space-y-4">
                          <div>
                            <label className="text-[11px] text-muted-foreground font-medium mb-1.5 block">What do you want to achieve?</label>
                            <input
                              value={heroGoal}
                              onChange={(e) => setHeroGoal(e.target.value)}
                              className="surface-input w-full h-9 px-3 rounded-lg text-sm"
                              placeholder="e.g. Align on Q2 product priorities"
                            />
                          </div>
                          {!heroShowContext ? (
                            <button
                              type="button"
                              className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground"
                              onClick={() => setHeroShowContext(true)}
                            >
                              <ChevronDown className="w-3 h-3" />
                              Add more context
                            </button>
                          ) : (
                            <div className="space-y-3">
                              <div>
                                <label className="text-[11px] text-muted-foreground font-medium mb-1 block">Additional context</label>
                                <textarea
                                  value={heroContext}
                                  onChange={(e) => setHeroContext(e.target.value)}
                                  className="surface-input w-full px-3 py-2 rounded-lg text-xs resize-none"
                                  rows={2}
                                  placeholder="Background info for a better agenda"
                                />
                              </div>
                              <div>
                                <label className="text-[11px] text-muted-foreground font-medium mb-1 block">Desired outcomes</label>
                                <textarea
                                  value={heroOutcomes}
                                  onChange={(e) => setHeroOutcomes(e.target.value)}
                                  className="surface-input w-full px-3 py-2 rounded-lg text-xs resize-none"
                                  rows={2}
                                  placeholder="What should be decided by the end?"
                                />
                              </div>
                            </div>
                          )}
                          {heroGoal.trim() && heroAgendaItems.length === 0 && (
                            <Button
                              type="button"
                              size="sm"
                              className="w-full gap-2"
                              onClick={generateHeroAgenda}
                              disabled={heroGenerating}
                            >
                              {heroGenerating ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Sparkles className="w-4 h-4" />
                              )}
                              {heroGenerating ? "Generating…" : "Generate agenda"}
                            </Button>
                          )}
                          {heroAgendaItems.length > 0 && (
                            <p className="text-[11px] text-muted-foreground">
                              {heroAgendaItems.length} agenda item{heroAgendaItems.length !== 1 ? "s" : ""} ready
                            </p>
                          )}
                          <div>
                            <SourceSelector eventId={heroEvent.event_id} compact />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    {(() => {
                      try {
                        if (isPast(parseISO(heroEvent.end_time))) {
                          return (
                            <Button
                              data-testid={`review-btn-${heroEvent.event_id}`}
                              variant="outline"
                              className="rounded-full h-9 text-sm gap-2 px-5"
                              onClick={(e) => { e.stopPropagation(); navigate(`/meetings/${heroEvent.event_id}/review`); }}
                            >
                              <FileText className="w-4 h-4" />
                              View Summary
                            </Button>
                          );
                        }
                      } catch {}
                      return (
                        <Button
                          data-testid={`join-btn-${heroEvent.event_id}`}
                          className="rounded-full h-9 text-sm gap-2 px-5"
                          onClick={(e) => { e.stopPropagation(); handleJoinMeeting(heroEvent); }}
                        >
                          Join Meeting
                          <ArrowRight className="w-3.5 h-3.5" />
                        </Button>
                      );
                    })()}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Upcoming meetings */}
            {restUpcoming.length > 0 && (
              <section>
                <h2 className="font-heading text-foreground text-base mb-5 flex items-center gap-2">
                  Upcoming
                  <span className="text-xs font-normal text-muted-foreground font-sans">{restUpcoming.length}</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {restUpcoming.map((evt, i) => (
                    <motion.div
                      key={evt.event_id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25, delay: i * 0.04 }}
                      className="surface-card-hover rounded-2xl p-5 flex flex-col cursor-pointer group"
                      onClick={() => handleJoinMeeting(evt)}
                      data-testid={`event-card-${evt.event_id}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <StatusPill startTime={evt.start_time} endTime={evt.end_time} />
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`event-menu-${evt.event_id}`} onClick={(e) => e.stopPropagation()}>
                              <MoreVertical className="w-3.5 h-3.5 text-muted-foreground" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="rounded-xl">
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger className="rounded-lg" onClick={(e) => e.stopPropagation()}>
                                <Target className="w-4 h-4 mr-2" />
                                Add goal & context
                              </DropdownMenuSubTrigger>
                              <DropdownMenuSubContent align="start" className="rounded-xl p-0">
                                <GoalContextPopover eventId={evt.event_id} onSaved={() => fetchEvents()} />
                              </DropdownMenuSubContent>
                            </DropdownMenuSub>
                            <DropdownMenuItem
                              data-testid={`delete-event-${evt.event_id}`}
                              className="text-destructive rounded-lg"
                              onClick={(e) => { e.stopPropagation(); deleteEvent(evt.event_id); }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <h3 className="font-heading text-foreground text-[15px] mb-1.5 line-clamp-2">{evt.title}</h3>

                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-auto pt-3">
                        <span className="flex items-center gap-1 font-mono">
                          <Clock className="w-3 h-3" />
                          {(() => {
                            try { return format(parseISO(evt.start_time), "h:mm a"); }
                            catch { return "TBD"; }
                          })()}
                        </span>
                        <span className="text-muted-foreground/50">{getTimeLabel(evt.start_time)}</span>
                        {evt.attendees?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {evt.attendees.length}
                          </span>
                        )}
                      </div>

                      <div className="mt-4 pt-3 border-t border-border">
                        <Button
                          data-testid={`join-btn-${evt.event_id}`}
                          variant="ghost"
                          className="rounded-full h-7 text-xs gap-1 px-0 text-muted-foreground hover:text-foreground w-full justify-start"
                          onClick={(e) => { e.stopPropagation(); handleJoinMeeting(evt); }}
                        >
                          Join Meeting
                          <ChevronRight className="w-3 h-3" />
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

            {/* Past meetings — compact summary rows */}
            {past.length > 0 && (
              <section>
                <h2 className="font-heading text-muted-foreground text-sm mb-4 flex items-center gap-2">
                  Past
                  <span className="text-xs font-normal">{past.length}</span>
                </h2>
                <div className="surface-card rounded-2xl divide-y divide-border overflow-hidden">
                  {past.map((evt, i) => (
                    <motion.div
                      key={evt.event_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2, delay: i * 0.03 }}
                      className="flex items-center gap-4 px-5 py-3.5 hover:bg-secondary/50 transition-colors cursor-pointer group"
                      onClick={() => navigate(`/meetings/${evt.event_id}/review`)}
                      data-testid={`event-card-${evt.event_id}`}
                    >
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        <h4 className="text-sm text-foreground/70 font-medium truncate">{evt.title}</h4>
                        {evt.has_transcript && (
                          <span className="flex items-center gap-1 text-[10px] text-primary/80 bg-primary/10 rounded-full px-1.5 py-0.5 shrink-0">
                            <Mic className="w-2.5 h-2.5" />
                            Transcribed
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="text-xs text-muted-foreground font-mono hidden sm:inline">
                          {(() => {
                            try { return format(parseISO(evt.start_time), "MMM d"); }
                            catch { return ""; }
                          })()}
                        </span>
                        <span className="text-xs text-muted-foreground font-mono hidden md:inline">
                          {(() => {
                            try { return format(parseISO(evt.start_time), "h:mm a"); }
                            catch { return ""; }
                          })()}
                        </span>
                        {evt.attendees?.length > 0 && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground/60 hidden lg:flex">
                            <Users className="w-3 h-3" />
                            {evt.attendees.length}
                          </span>
                        )}
                        <Button
                          data-testid={`review-btn-${evt.event_id}`}
                          variant="ghost"
                          className="rounded-full h-7 text-xs gap-1 px-2 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => { e.stopPropagation(); navigate(`/meetings/${evt.event_id}/review`); }}
                        >
                          <FileText className="w-3 h-3" />
                          Summary
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`event-menu-${evt.event_id}`} onClick={(e) => e.stopPropagation()}>
                              <MoreVertical className="w-3.5 h-3.5 text-muted-foreground" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="rounded-xl">
                            <DropdownMenuItem
                              data-testid={`delete-event-${evt.event_id}`}
                              className="text-destructive rounded-lg"
                              onClick={(e) => { e.stopPropagation(); deleteEvent(evt.event_id); }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
